import { useState, useEffect } from 'react';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { UserPlus, Pencil, Trash, ShieldCheck } from '@phosphor-icons/react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const ROLES = ['admin', 'inventory_manager', 'finance', 'viewer'];
const PERMISSIONS = ['orders.view', 'orders.edit', 'products.view', 'products.edit', 'inventory.view', 'inventory.edit', 'reports.view', 'settings.view'];

export default function StaffManagementPage() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', email: '', role_type: 'viewer', permissions: [] });

  useEffect(() => { fetchUsers(); }, []);

  const fetchUsers = async () => {
    try {
      const { data } = await axios.get(`${API}/api/seller/internal-users`, { withCredentials: true });
      setUsers(data.users || []);
    } catch { toast.error('Failed to load staff'); }
    finally { setLoading(false); }
  };

  const handleSave = async () => {
    try {
      if (editing) {
        await axios.patch(`${API}/api/seller/internal-users/${editing.internal_user_id}`, form, { withCredentials: true });
        toast.success('Staff updated');
      } else {
        await axios.post(`${API}/api/seller/internal-users`, form, { withCredentials: true });
        toast.success('Staff added');
      }
      setDialogOpen(false);
      setEditing(null);
      setForm({ name: '', email: '', role_type: 'viewer', permissions: [] });
      fetchUsers();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save'); }
  };

  const handleDelete = async (uid) => {
    if (!window.confirm('Remove this staff member?')) return;
    try {
      await axios.delete(`${API}/api/seller/internal-users/${uid}`, { withCredentials: true });
      toast.success('Staff removed');
      fetchUsers();
    } catch { toast.error('Failed to delete'); }
  };

  const togglePerm = (perm) => {
    setForm(f => ({ ...f, permissions: f.permissions.includes(perm) ? f.permissions.filter(p => p !== perm) : [...f.permissions, perm] }));
  };

  const roleColor = { admin: 'bg-purple-100 text-purple-700', inventory_manager: 'bg-blue-100 text-blue-700', finance: 'bg-green-100 text-green-700', viewer: 'bg-gray-100 text-gray-600' };

  return (
    <DashboardLayout>
      <div className="p-6 max-w-6xl" data-testid="staff-management-page">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-[#0A0A0A]">Staff Management</h1>
            <p className="text-sm text-[#6b6b8a] mt-1">Manage internal team members and their permissions</p>
          </div>
          <Button data-testid="add-staff-btn" onClick={() => { setEditing(null); setForm({ name: '', email: '', role_type: 'viewer', permissions: [] }); setDialogOpen(true); }} className="bg-[#7d64ff] hover:bg-[#6b4fef]">
            <UserPlus size={16} className="mr-2" /> Add Staff
          </Button>
        </div>

        {loading ? <div className="text-center py-12 text-[#9c9db7]">Loading...</div> : users.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-xl border border-[#e5e5ea]">
            <ShieldCheck size={48} className="mx-auto text-[#9c9db7] mb-3" />
            <p className="text-[#6b6b8a]">No staff members yet. Add your first team member.</p>
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-[#e5e5ea] overflow-hidden">
            <table className="w-full text-sm" data-testid="staff-table">
              <thead className="bg-[#f9f9fb] border-b border-[#e5e5ea]">
                <tr>
                  <th className="text-left px-4 py-3 font-medium text-[#6b6b8a]">Name</th>
                  <th className="text-left px-4 py-3 font-medium text-[#6b6b8a]">Email</th>
                  <th className="text-left px-4 py-3 font-medium text-[#6b6b8a]">Role</th>
                  <th className="text-left px-4 py-3 font-medium text-[#6b6b8a]">Status</th>
                  <th className="text-right px-4 py-3 font-medium text-[#6b6b8a]">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map(u => (
                  <tr key={u.internal_user_id} className="border-b border-[#f0f0f0] hover:bg-[#fafafe]">
                    <td className="px-4 py-3 font-medium text-[#0A0A0A]">{u.name}</td>
                    <td className="px-4 py-3 text-[#6b6b8a]">{u.email}</td>
                    <td className="px-4 py-3"><span className={`px-2 py-0.5 rounded-full text-xs font-medium ${roleColor[u.role_type] || 'bg-gray-100'}`}>{u.role_type?.replace('_', ' ')}</span></td>
                    <td className="px-4 py-3"><span className={`px-2 py-0.5 rounded-full text-xs font-medium ${u.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>{u.is_active ? 'Active' : 'Inactive'}</span></td>
                    <td className="px-4 py-3 text-right">
                      <Button variant="ghost" size="sm" data-testid={`edit-staff-${u.internal_user_id}`} onClick={() => { setEditing(u); setForm({ name: u.name, email: u.email, role_type: u.role_type, permissions: u.permissions || [] }); setDialogOpen(true); }}><Pencil size={14} /></Button>
                      <Button variant="ghost" size="sm" data-testid={`delete-staff-${u.internal_user_id}`} onClick={() => handleDelete(u.internal_user_id)} className="text-red-500 hover:text-red-700"><Trash size={14} /></Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-md" data-testid="staff-dialog">
            <DialogHeader><DialogTitle>{editing ? 'Edit Staff' : 'Add Staff Member'}</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div><Label>Name</Label><Input data-testid="staff-name" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Full name" /></div>
              <div><Label>Email</Label><Input data-testid="staff-email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} placeholder="email@example.com" disabled={!!editing} /></div>
              <div><Label>Role</Label>
                <select data-testid="staff-role" value={form.role_type} onChange={e => setForm(f => ({ ...f, role_type: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm">
                  {ROLES.map(r => <option key={r} value={r}>{r.replace('_', ' ')}</option>)}
                </select>
              </div>
              <div><Label>Permissions</Label>
                <div className="grid grid-cols-2 gap-2 mt-1">
                  {PERMISSIONS.map(p => (
                    <label key={p} className="flex items-center gap-2 text-xs cursor-pointer">
                      <input type="checkbox" checked={form.permissions.includes(p)} onChange={() => togglePerm(p)} className="rounded" />
                      {p}
                    </label>
                  ))}
                </div>
              </div>
              <Button data-testid="save-staff-btn" onClick={handleSave} className="w-full bg-[#7d64ff] hover:bg-[#6b4fef]">{editing ? 'Update' : 'Add'} Staff</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
